import React from "react"
import Image from "next/image"

import type { ItemType } from "@shared/graphql.types"
import { useCurrencySymbol } from "hooks"
import { Link } from "components/Link"

import { useUser } from "lib/user"
import {
  container,
  detailsWrapper,
  wrapper,
  details,
  productSize,
  imageWrapper,
  productShade,
  productPricing,
  primaryPrice,
  secondaryPrice,
  productPrices,
  numbersWrapper,
  quantityWrapper,
} from "./SimpleProductCard.css"
import { formatPriceNumber } from "utils/prices"

type Props = {
  item: ItemType
  href: string
}

export const SimpleProductCardV2: React.FC<Props> = ({ item, href }) => {
  const currencySymbol = useCurrencySymbol()
  const { hasMembership, hasMembershipInBag, isMember } = useUser()
  const hasMemberExperience = hasMembership || hasMembershipInBag
  const pricingLabel = hasMemberExperience ? "Member's Price" : ""

  const firstMedia = item?.variant?.detailsMedia || item?.variant?.media?.[0]

  const imageUrl = firstMedia?.thumbnails?.size_240 ?? ""
  const imageAlt = firstMedia?.description || "Beautypie Product"

  const imageTitle = firstMedia?.description || "Beautypie Product"

  return (
    <div data-testid="simple-product-card" className={container}>
      <div className={wrapper}>
        <SimpleProductCardImage
          href={href}
          imageUrl={imageUrl}
          imageAlt={imageAlt}
          imageTitle={imageTitle}
        />

        <div className={detailsWrapper}>
          <div className={details}>
            <Link href={href} variant="acuminMedium" mt="0" mb="4px">
              {item.variant?.brandName}
            </Link>

            <Link
              href={href}
              mt={0}
              variant={
                !item.variant?.brandName ? "acuminMedium" : "acuminLight"
              }
            >
              {item?.variant?.title}
            </Link>

            {item?.variant?.subTitle && (
              <p className={productShade}>{item.variant.subTitle}</p>
            )}

            <p className={productSize}>{item?.variant?.size}</p>

            <div className={productPricing}>
              <span>{pricingLabel}</span>
            </div>
          </div>
          <div className={numbersWrapper}>
            <p className={quantityWrapper}>Qty {item.quantity}</p>
            <div className={productPrices}>
              {hasMemberExperience ? (
                <>
                  <span className={secondaryPrice}>
                    {currencySymbol}
                    {formatPriceNumber(
                      isMember
                        ? item?.variant?.pricing.typical
                        : item?.variant?.pricing.guest
                    )}
                  </span>
                  <span className={primaryPrice}>
                    {currencySymbol}
                    {formatPriceNumber(item?.variant?.pricing.members)}
                  </span>
                </>
              ) : (
                <span className={primaryPrice}>
                  {currencySymbol}
                  {formatPriceNumber(item?.variant?.pricing.guest)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

type CardImageProps = {
  href: string
  imageUrl: string
  imageAlt: string
  imageTitle: string
}

const SimpleProductCardImage: React.FC<CardImageProps> = ({
  href,
  imageUrl,
  imageAlt,
  imageTitle,
}) => {
  return (
    <Link href={href}>
      <div className={imageWrapper}>
        <Image
          src={imageUrl}
          alt={imageAlt}
          title={imageTitle}
          fill
          sizes="(max-width: 768px) 5.6rem, (max-width: 1200px) 6rem"
          style={{
            objectFit: "cover",
            objectPosition: "center",
          }}
        />
      </div>
    </Link>
  )
}
